import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { compact } from 'lodash-es';
import { matchSorter } from 'match-sorter';
import { Typography, Autocomplete } from 'seagull';
import { Plus } from 'seagull/icons';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useLocaleKeys } from '~/i18n';
import { assertUnreachable } from '~/infra/utils';
import { ContactModel } from '~/models/ContactModel';
import styles from './ContactPicker.module.scss';
const staticOptions = {
    emptyState: {
        type: 'emptyState'
    },
    initial: {
        type: 'initial'
    },
    newContact: {
        type: 'newContact'
    },
    sampleContact: {
        type: 'sampleContact'
    }
};
export const contactPickerTestId = 'agreement-studio-contact-picker';
export default function ContactPicker(props) {
    const { t: contactTexts } = useLocaleKeys('components.contactsSelector');
    const noOptionsText = contactTexts.noOptionsText();
    const { value, error, helperText, label = contactTexts.defaultLabel(), disabled, testId = contactPickerTestId, onChange, hideSampleContact = false, filterContact, fullWidth = false, showErrorOnAnySelection = false } = props;
    const { contacts } = useContacts();
    const existingContactsOptions = contacts.filter((c)=>!c.isSample() && c.isActive()).filter((c)=>filterContact ? filterContact(c) : true).map((c)=>({
            type: 'existingContact',
            contact: c.toJSON()
        }));
    const options = [
        staticOptions.initial,
        staticOptions.emptyState,
        staticOptions.newContact,
        staticOptions.sampleContact,
        ...existingContactsOptions
    ];
    let currentOption;
    if (value.isSample()) {
        currentOption = {
            type: 'sampleContact'
        };
    } else if (value instanceof ContactModel) {
        currentOption = {
            type: 'existingContact',
            contact: value.toJSON()
        };
    } else if (value.hasPartialContactDetails()) {
        currentOption = staticOptions.newContact;
    } else {
        currentOption = staticOptions.initial;
    }
    const showError = error === true && (showErrorOnAnySelection || currentOption.type === 'initial');
    return /*#__PURE__*/ _jsx(Autocomplete, {
        bi: {
            name: 'contact_picker'
        },
        fullWidth: fullWidth,
        testId: testId,
        textFieldProps: {
            label,
            placeholder: contactTexts.placeholder(),
            helperText: showError && helperText,
            error: showError
        },
        options: options,
        disabled: disabled,
        value: currentOption,
        onChange: (_, newValue)=>{
            if (!newValue || typeof newValue === 'string' || Array.isArray(newValue)) {
                onChange({
                    type: 'initial'
                });
                return;
            }
            switch(newValue.type){
                case 'newContact':
                    onChange({
                        type: 'newContact'
                    });
                    break;
                case 'existingContact':
                    onChange({
                        type: 'existingContact',
                        id: newValue.contact.id
                    });
                    break;
                case 'sampleContact':
                    onChange({
                        type: 'sampleContact'
                    });
                    break;
                case 'emptyState':
                    break;
                case 'initial':
                    onChange({
                        type: 'initial'
                    });
                    break;
                default:
                    assertUnreachable(newValue);
            }
        },
        renderOption: (option)=>{
            switch(option.type){
                case 'existingContact':
                    return /*#__PURE__*/ _jsxs(Typography, {
                        as: "p",
                        children: [
                            option.contact.companyName,
                            /*#__PURE__*/ _jsxs(Typography, {
                                as: "div",
                                skin: "secondary",
                                children: [
                                    option.contact.firstName,
                                    " ",
                                    option.contact.lastName,
                                    " (",
                                    option.contact.email,
                                    ")"
                                ]
                            })
                        ]
                    });
                case 'emptyState':
                    return /*#__PURE__*/ _jsx(Typography, {
                        testId: "contact-picker-empty",
                        children: noOptionsText
                    });
                case 'newContact':
                    return /*#__PURE__*/ _jsxs(Typography, {
                        skin: "link",
                        testId: "contact-picker-add-contact",
                        className: styles.addContact,
                        children: [
                            /*#__PURE__*/ _jsx(Plus, {}),
                            contactTexts.newContact()
                        ]
                    });
                case 'sampleContact':
                    return /*#__PURE__*/ _jsxs(Typography, {
                        testId: "contact-picker-sample-contact",
                        ellipsis: true,
                        skin: "link",
                        children: [
                            contactTexts.sampleContact(),
                            ' ',
                            /*#__PURE__*/ _jsx(Typography, {
                                skin: "secondary",
                                as: "span",
                                children: contactTexts.sampleContactSubtitle()
                            })
                        ]
                    });
                default:
                    return null;
            }
        },
        getOptionSelected: (option, value)=>{
            if (option.type === 'existingContact' && value.type === 'existingContact') {
                return option.contact.id === value.contact.id;
            } else {
                return option.type === value.type;
            }
        },
        getOptionLabel: (option)=>{
            switch(option.type){
                case 'existingContact':
                    return "".concat(option.contact.companyName, ", ").concat(option.contact.firstName, " ").concat(option.contact.lastName, " (").concat(option.contact.email, ")");
                case 'emptyState':
                    return noOptionsText;
                case 'newContact':
                    return contactTexts.newContact();
                case 'sampleContact':
                    return "".concat(contactTexts.sampleContact(), " ").concat(contactTexts.sampleContactSubtitle());
                default:
                    return '';
            }
        },
        filterOptions: (options, param)=>{
            let { inputValue } = param;
            const baseOptions = hideSampleContact ? [
                staticOptions.newContact
            ] : [
                staticOptions.newContact,
                staticOptions.sampleContact
            ];
            if (contacts.length === 0) {
                return compact([
                    inputValue && staticOptions.emptyState,
                    ...baseOptions
                ]);
            }
            const filteredContacts = matchSorter(options, inputValue, {
                keys: [
                    'contact.companyName',
                    'contact.firstName',
                    'contact.lastName',
                    'contact.email'
                ]
            });
            return filteredContacts.length > 0 ? [
                ...baseOptions,
                ...filteredContacts
            ] : [
                staticOptions.emptyState,
                ...baseOptions
            ];
        },
        noOptionsText: noOptionsText,
        getOptionDisabled: (option)=>option.type === 'emptyState',
        selectOnFocus: true
    });
}
